import { useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Plus } from 'lucide-react'

import type { PackagingLogEntry } from '@/lib/api/packaging-log-api'
import { Button } from '@/components/ui/button'
import {
    HEADER_CELL,
    LEDGER_COLS,
    LEDGER_MIN_W,
} from '@/components/packaging-log/ledger-grid'
import { LogEntryEditRow } from '@/components/packaging-log/log-entry-edit-row'
import {
    LogEntryRow,
    packedKg,
    takenKg,
} from '@/components/packaging-log/log-entry-row'
import { LedgerDayPager } from '@/components/common/ledger-day-pager'
import { useAuth } from '@/hooks/use-auth'
import { useFetchAllPages } from '@/hooks/use-fetch-all-pages'
import { usePackagingLogEntriesInfinite } from '@/hooks/use-packaging-log-query'
import { usePackagingLogApprovedSse } from '@/hooks/use-packaging-log-approved-sse'
import { todayYMD } from '@/lib/date-presets'
import { hasLedgerDraft, ledgerDraftKey } from '@/lib/ledger-draft'
import { formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/sales-warehouse/packaging-log/',
)({
    component: PackagingLogPage,
})

function PackagingLogPage() {
    const { hasPermission, user } = useAuth()
    const canWriteNotes = hasPermission?.('product_lots.write') ?? true
    const canWriteLog = hasPermission?.('packaging_logs.write') ?? true
    // Mở màn phiếu cần quyền xem kho; không có thì mã phiếu chỉ là chữ (M-61).
    const canOpenNotes = hasPermission?.('product_lots.read') ?? true

    // Thủ kho duyệt phiếu của mẻ → sổ tải lại để nút in tem hiện ngay (M-87).
    usePackagingLogApprovedSse()

    // Sổ lật theo NGÀY như sổ giấy ngoài xưởng: mỗi trang là trọn một ngày.
    const [day, setDay] = useState(todayYMD)
    const query = usePackagingLogEntriesInfinite({
        date_from: day,
        date_to: day,
    })
    const { data, isLoading } = query
    // Ngày cao điểm có thể vượt một trang API — kéo nốt cho đủ sổ, vì "còn
    // trang nữa" không phải thứ người ghi sổ phải bấm.
    useFetchAllPages(query)
    const entries = useMemo(
        () => (data?.pages ?? []).flatMap((p) => p.data ?? []),
        [data],
    )

    // Nhập ngay trong bảng: `adding` là dòng trống ở đầu, `editingId` là mẻ đang
    // được sửa tại chỗ. Hai thứ loại trừ nhau để không có hai dòng nhập cùng lúc.
    //
    // M-196: mẻ mới đang nhập dở được giữ nháp trên máy (theo sổ + tài khoản).
    // Quay lại sổ mà còn nháp thì mở sẵn dòng nhập để ghi tiếp.
    const draftKey = user
        ? ledgerDraftKey('packaging_12g5', user.id)
        : undefined
    const [adding, setAdding] = useState(
        () => canWriteLog && hasLedgerDraft(draftKey),
    )
    const [editingId, setEditingId] = useState<string | undefined>()
    // Quyền ghi / tài khoản có thể tới SAU lần vẽ đầu (vừa đăng nhập lại: phiên
    // mới nạp quyền rỗng rồi mới tải đủ). Đổi thì xét lại việc mở dòng nháp —
    // điều chỉnh state ngay lúc render, không qua effect.
    const draftGate = `${canWriteLog}|${draftKey ?? ''}`
    const [seenDraftGate, setSeenDraftGate] = useState(draftGate)
    if (seenDraftGate !== draftGate) {
        setSeenDraftGate(draftGate)
        if (!canWriteLog) setAdding(false)
        else if (!editingId && hasLedgerDraft(draftKey)) setAdding(true)
    }

    const startAdd = () => {
        setEditingId(undefined)
        setAdding(true)
    }
    const startEdit = (entry: PackagingLogEntry) => {
        setAdding(false)
        setEditingId(entry.id)
    }
    // `savedDay` là ngày của mẻ vừa lưu: dòng nhập giữ nguyên ngày lúc mở, nên
    // người ghi có thể lật sang trang khác rồi mới bấm Lưu. Không nhảy về đúng
    // ngày đó thì bảng không đổi gì sau khi Lưu và họ tưởng mất công, ghi lại
    // lần nữa thành mẻ trùng.
    //
    // Đóng xong mà nháp mẻ mới vẫn còn (vừa sửa một mẻ cũ trong lúc mẻ mới đang
    // dở) thì mở lại dòng nhập dở. Lưu/Huỷ mẻ mới đã xoá nháp trước khi gọi tới
    // đây nên không bị mở lại.
    const closeAll = () => {
        setAdding(canWriteLog && hasLedgerDraft(draftKey))
        setEditingId(undefined)
    }
    // Sau khi LƯU thì còn phải nhảy sang đúng ngày của mẻ vừa lưu: dòng nhập giữ
    // nguyên ngày lúc mở, nên người ghi có thể lật sang trang khác rồi mới bấm
    // Lưu. Không nhảy thì bảng không đổi gì và họ tưởng mất công, ghi lại lần
    // nữa thành mẻ trùng.
    //
    // Hàm RIÊNG chứ không thêm tham số cho `closeAll`: `onCancel={closeAll}` nối
    // thẳng vào onClick nên React truyền MouseEvent vào tham số đầu — nhận nhầm
    // nó làm ngày là vỡ cả trang.
    const doneAt = (savedDay?: string) => {
        if (typeof savedDay === 'string' && savedDay) setDay(savedDay)
        closeAll()
    }

    const waiting = entries.filter((e) => e.sync_state !== 'synced')
    const divergedCount = entries.filter(
        (e) => e.sync_state === 'diverged',
    ).length
    const totalOut = waiting.reduce((s, e) => s + takenKg(e), 0)
    const totalIn = waiting.reduce((s, e) => s + packedKg(e), 0)

    return (
        <div className="ledger-text space-y-4 page-pad">
            <header className="border-b-2 border-foreground pb-3">
                <h1 className="text-2xl font-bold md:text-3xl">
                    {m.pkg_log_title()}
                </h1>
                <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                    {m.pkg_log_subtitle()}
                </p>
            </header>

            <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2 border-b pb-3">
                <span className="flex items-baseline gap-2">
                    <b className="text-xl tabular-nums">{waiting.length}</b>
                    <span className="text-sm text-muted-foreground">
                        {m.pkg_log_stat_rows()}
                    </span>
                </span>
                <span className="flex items-baseline gap-2">
                    <b className="text-xl tabular-nums">
                        {formatQty(totalOut)}
                    </b>
                    <span className="text-sm text-muted-foreground">
                        {m.pkg_log_stat_out()}
                    </span>
                </span>
                <span className="flex items-baseline gap-2">
                    <b className="text-xl tabular-nums">{formatQty(totalIn)}</b>
                    <span className="text-sm text-muted-foreground">
                        {m.pkg_log_stat_in()}
                    </span>
                </span>
                {divergedCount > 0 && (
                    <span className="rounded-full bg-destructive/15 px-3 py-1 text-sm font-semibold text-destructive">
                        {m.pkg_log_diverged_count({ n: divergedCount })}
                    </span>
                )}
                {/* Ba số trên chỉ cộng các mẻ ĐÃ TẢI, không phải toàn bộ sổ. */}
                <span className="text-xs text-muted-foreground">
                    {m.pkg_log_stat_scope({ n: entries.length })}
                </span>
            </div>

            <LedgerDayPager value={day} onChange={setDay} />

            {/* Nút thêm nằm TRÊN bảng và hiện ở MỌI ngày: người ghi sổ đang mở
                trang nào cũng phải ghi được mẻ mới ngay, không phải lật về hôm
                nay trước. */}
            {canWriteLog && !adding && !editingId && (
                <Button
                    type="button"
                    variant="outline"
                    className="h-12"
                    onClick={startAdd}
                >
                    <Plus className="mr-1 size-4" />
                    {m.pkg_log_btn_add_row()}
                </Button>
            )}

            {/* Bảng sổ — kẻ ô như sổ giấy của xưởng. */}
            <section className="overflow-x-auto rounded-md border bg-card">
                <div className={LEDGER_MIN_W}>
                    <div
                        className={`grid gap-0 border-b bg-muted/40 text-xs font-semibold uppercase tracking-wide text-muted-foreground ${LEDGER_COLS}`}
                    >
                        <div className={HEADER_CELL}>
                            {m.pkg_log_col_date()}
                        </div>
                        <div className={HEADER_CELL}>
                            {m.pkg_log_col_source_lot()}
                        </div>
                        <div className={`${HEADER_CELL} text-right`}>
                            {m.pkg_log_col_source_kg()}
                        </div>
                        <div className={HEADER_CELL}>
                            {m.pkg_log_col_product()}
                        </div>
                        <div className={`${HEADER_CELL} text-right`}>
                            {m.pkg_log_col_qty()}
                        </div>
                        <div className={`${HEADER_CELL} text-right`}>
                            {m.pkg_log_col_discount()}
                        </div>
                        <div className={HEADER_CELL}>
                            {m.pkg_log_col_status()}
                        </div>
                    </div>

                    {isLoading && (
                        <p className="p-6 text-center text-muted-foreground">
                            {m.pkg_log_loading()}
                        </p>
                    )}
                    {!isLoading && entries.length === 0 && !adding && (
                        <p className="p-6 text-center text-muted-foreground">
                            {m.pkg_log_empty()}
                        </p>
                    )}

                    {/* Dòng đang nhập dở nằm ở ĐẦU bảng và theo người dùng
                        sang mọi ngày — đổi trang giữa chừng mà mất dòng đang gõ
                        là mất luôn công ghi. Ngày mặc định của mẻ mới là ngày
                        đang xem, không phải hôm nay. */}
                    {adding && (
                        <LogEntryEditRow
                            // Đổi tài khoản thì dựng lại form từ nháp của người đó.
                            key={draftKey}
                            defaultDate={day}
                            draftKey={draftKey}
                            onDone={doneAt}
                            onCancel={closeAll}
                        />
                    )}

                    {entries.map((entry) =>
                        editingId === entry.id ? (
                            <LogEntryEditRow
                                key={entry.id}
                                entry={entry}
                                onDone={doneAt}
                                onCancel={closeAll}
                            />
                        ) : (
                            <LogEntryRow
                                key={entry.id}
                                entry={entry}
                                canWriteNotes={canWriteNotes}
                                canWriteLog={canWriteLog}
                                canOpenNotes={canOpenNotes}
                                onEdit={startEdit}
                            />
                        ),
                    )}
                </div>
            </section>
        </div>
    )
}
